import { useState, useEffect, useCallback } from 'react';
import { getOperationStatus, OperationStatus } from '../services/OperationTimeService';
import { useCart } from '../contexts/CartContext';

/**
 * 운영시간 체크 훅
 */
export const useOperationTime = () => {
  const [operationStatus, setOperationStatus] = useState<OperationStatus>({ isOpen: true });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const status = await getOperationStatus();
      setOperationStatus(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      // 에러 발생 시 기본적으로 운영 중으로 처리
      setOperationStatus({ isOpen: true, message: 'Error occurred, assuming service is open' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return {
    isOpen: operationStatus.isOpen,
    operationStatus,
    isLoading,
    error,
    recheckStatus: checkStatus
  };
};

/**
 * 운영시간 체크 후 콜백 실행하는 훅
 */
export const useOperationTimeCheck = () => {
  const { clearCart } = useCart();
  
  const checkAndExecute = useCallback(async (
    onOpen: () => void,
    onClosed?: () => void,
    clearCartOnClosed: boolean = false
  ) => {
    try {
      const status = await getOperationStatus();
      if (status.isOpen) {
        onOpen();
      } else {
        if (clearCartOnClosed) {
          clearCart();
        }
        onClosed?.();
      }
    } catch (error) {
      console.warn('Operation time check failed, executing open callback:', error);
      onOpen(); // 에러 시 기본적으로 허용
    }
  }, [clearCart]);

  return { checkAndExecute };
};

/**
 * 30분 전 안내를 위한 훅
 */
export const useServiceClosingNotification = () => {
  const [shouldShowClosingModal, setShouldShowClosingModal] = useState(false);
  const [operationEndTime, setOperationEndTime] = useState<string | null>(null);
  const [lastCheckedTime, setLastCheckedTime] = useState<number>(0);

  // 오늘 알림 표시 여부를 확인하는 함수
  const getTodayNotificationStatus = useCallback(() => {
    const today = new Date().toDateString();
    const shown = sessionStorage.getItem(`closingNotificationShown_${today}`);
    return shown === 'true';
  }, []);

  // 운영 종료 30분 전 시점인지 확인하는 함수
  const isApproachingClosingTime = useCallback((endTime: string): boolean => {
    try {
      const now = new Date();
      // endTime을 오늘 날짜의 시간으로 변환 (예: "16:29:00" -> 오늘 16:29:00)
      const [hours, minutes, seconds] = endTime.split(':').map(Number);
      const closingTime = new Date();
      closingTime.setHours(hours, minutes, seconds || 0, 0);
      
      // 30분 전 시점 계산
      const thirtyMinutesBefore = new Date(closingTime);
      thirtyMinutesBefore.setMinutes(thirtyMinutesBefore.getMinutes() - 30);
      
      // 현재 시간이 30분 전 시점을 넘었는지 확인
      const isAfterThirtyMinutesMark = now >= thirtyMinutesBefore;
      const isBeforeClosing = now < closingTime;
      
      
      return isAfterThirtyMinutesMark && isBeforeClosing;
    } catch (error) {
      console.error('[useServiceClosingNotification] Error parsing end time:', error);
      return false;
    }
  }, []);

  const checkClosingStatus = useCallback(async () => {
    // 이미 오늘 알림을 표시했다면 체크하지 않음
    if (getTodayNotificationStatus()) {
      return;
    }

    const now = Date.now();
    
    // 운영 종료 시간 정보가 없거나, 30분 전 시점이 아니라면 초기 체크만 수행
    if (!operationEndTime || !isApproachingClosingTime(operationEndTime)) {
      // 5분 이내에 이미 체크했다면 스킵 (불필요한 API 호출 방지)
      if (now - lastCheckedTime < 5 * 60 * 1000) {
        return;
      }
      
      try {
        const status = await getOperationStatus();
        setLastCheckedTime(now);
        
        // 운영 중이면 종료 시간 정보 저장
        if (status.isOpen && status.endTime) {
          setOperationEndTime(status.endTime);
        }
        
        // 서버에서 이미 isClosingSoon이 true라면 바로 모달 표시
        if (status.isOpen && status.isClosingSoon) {
          setShouldShowClosingModal(true);
          
          const today = new Date().toDateString();
          sessionStorage.setItem(`closingNotificationShown_${today}`, 'true');
        }
      } catch (error) {
        console.warn('[useServiceClosingNotification] Initial check failed:', error);
        setLastCheckedTime(now);
      }
      return;
    }

    try {
      const status = await getOperationStatus();
      setLastCheckedTime(now);
      
      // 서버에서 확인된 30분 전 상태에 따라 모달 표시
      if (status.isOpen && status.isClosingSoon) {
        setShouldShowClosingModal(true);
        
        const today = new Date().toDateString();
        sessionStorage.setItem(`closingNotificationShown_${today}`, 'true');
      }
    } catch (error) {
      console.warn('[useServiceClosingNotification] Server check failed:', error);
      setLastCheckedTime(now);
    }
  }, [getTodayNotificationStatus, operationEndTime, isApproachingClosingTime, lastCheckedTime]);

  const dismissClosingModal = useCallback(() => {
    setShouldShowClosingModal(false);
  }, []);

  return {
    shouldShowClosingModal,
    checkClosingStatus,
    dismissClosingModal,
    hasShownClosingNotification: getTodayNotificationStatus()
  };
};
